'use client';

import { Box, Flex, Popover, Typography } from '@coolblue-development/freddy';
import selectPropertyValueTrigger from '@coolblue-development/ga4-events/triggers/ProductCollection/selectPropertyValueTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { AvailabilityStates } from '@coolblue-development/next-availability-state';
import { useFreddyTranslations } from '@coolblue-development/next-design-system-utils/client';
import { SelectableBox, SelectableBoxGroup } from '@coolblue-development/next-patterns/client';
import type { Product as GlobalProduct } from '@features/product/typings';
import { useIsLeasePage } from '@hooks/useIsLeasePage';
import { useTranslation } from '@i18n/useTranslation';
import { getPopoverText } from '@utils/collectionUtils';
import { useRouter } from 'next/navigation';
import { Fragment, useRef, useState } from 'react';

import {
  hasHandoverSessionCookie,
  renewHandoverSessionCookie
} from '../../OrderBlock/BusinessLeasing/HandoverSession';
import { type Discriminant, PresentationFormat, type Product, Variant } from '../Collection.types';
import { PropertyCard } from './PropertyCard';
import { PropertyTitle } from './PropertyTitle';

type PropertyCardsProps = {
  discriminant: Discriminant;
  mainImageId?: string;
  onProductClick?: (product: Product | null) => void;
  product: GlobalProduct;
  shouldShowCollectionTitle: boolean;
  variant: Variant;
};

export const PropertyCards = ({
  discriminant,
  mainImageId,
  onProductClick,
  product,
  shouldShowCollectionTitle,
  variant
}: PropertyCardsProps) => {
  const router = useRouter();
  const { trackEvent } = useAnalytics();
  const { t } = useTranslation('webshop.productPage');
  const popoverTranslations = useFreddyTranslations('popover');

  const cardsWithBundle = discriminant.presentationFormat === PresentationFormat.CARDS_WITH_BUNDLE;
  const cardsWithDiscount =
    discriminant.presentationFormat === PresentationFormat.CARDS_WITH_DISCOUNTS;
  const selectedValue = discriminant.values.find(value => value.isValueOfCurrentProduct);
  const isMobile = variant === Variant.MOBILE;
  const isDesktop = variant === Variant.DESKTOP;

  const [popoverState, setPopoverState] = useState<{ [key: number]: boolean }>({});
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  const handleMouseEnter = (index: number) => {
    setPopoverState(prevState => ({ ...prevState, [index]: true }));
  };

  const handleMouseLeave = (index: number) => {
    setPopoverState(prevState => ({ ...prevState, [index]: false }));
  };

  const isLeasePage = useIsLeasePage();

  const handleClick = (product: Product | null, index: number, propertyValue: string) => {
    if (product) {
      trackEvent(
        selectPropertyValueTrigger({
          sub_feature: 'multi_discriminant_selector',
          index: index + 1,
          total_indices: discriminant.values.length,
          property_name: discriminant.displayName,
          property_value: propertyValue,
          item_id: product.id
        })
      );
      let redirectUrl = product.urls.product;

      if (isLeasePage) {
        redirectUrl += '?lease=1';
        if (hasHandoverSessionCookie()) {
          renewHandoverSessionCookie();
          redirectUrl += '&bho=1';
        }
      }

      if (redirectUrl && isDesktop) {
        router.push(redirectUrl);
      }
      if (onProductClick && isMobile) {
        onProductClick(product);
      }
    }
  };

  return (
    <>
      {discriminant && (
        <PropertyTitle
          discriminant={discriminant}
          product={product}
          shouldShowCollectionTitle={shouldShowCollectionTitle}
          variant={variant}
        />
      )}
      <Flex gap={200} direction={cardsWithBundle ? 'column' : 'row'} wrap='wrap'>
        <SelectableBoxGroup
          name='propertyCardsCollection'
          type='radio'
          spacing='compact'
          defaultValue={selectedValue?.id}
        >
          {discriminant.values.map((value, valueIndex) => {
            const showPopover =
              isDesktop &&
              (value.product?.nullableAvailabilityState ===
                AvailabilityStates.TemporaryOutOfStock ||
                value.product?.nullableAvailabilityState === AvailabilityStates.OutOfStock ||
                !value.product);

            if (!value.name) return <Fragment key={valueIndex}></Fragment>;

            return (
              <Fragment key={valueIndex}>
                <Box
                  ref={el => {
                    if (el) {
                      cardRefs.current[valueIndex] = el;
                    }
                  }}
                  onMouseEnter={() => handleMouseEnter(valueIndex)}
                  onMouseLeave={() => handleMouseLeave(valueIndex)}
                >
                  <SelectableBox
                    value={value.id}
                    onClick={() => handleClick(value.product, valueIndex, value.name)}
                    disabled={!value.product || !value.product.shouldRedirect}
                  >
                    <PropertyCard
                      value={value}
                      cardsWithBundle={cardsWithBundle}
                      cardsWithDiscount={cardsWithDiscount}
                      mainImageId={mainImageId}
                    />
                  </SelectableBox>
                </Box>

                {popoverState[valueIndex] && showPopover && (
                  <Popover
                    translations={popoverTranslations}
                    referenceElement={cardRefs.current[valueIndex]}
                    open={popoverState[valueIndex]}
                    onClose={() => handleMouseLeave(valueIndex)}
                    withCloseButton={false}
                    placement='top-start'
                  >
                    <Typography variant='body' sx={{ whiteSpace: 'nowrap' }}>
                      {t(getPopoverText(value.product))}
                    </Typography>
                  </Popover>
                )}
              </Fragment>
            );
          })}
        </SelectableBoxGroup>
      </Flex>
    </>
  );
};
